import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { AuthLayout } from "../components/AuthLayout";

/**
 * /merchant/recover/verify?id=…&token=… — Phase 17 second-factor +
 * token rotation page (D53, D60, D61).
 *
 * Wizard:
 *   1. On mount: POST /merchant/recover/verify-link with the
 *      (requestId, token) pair from the URL. Server burns the link
 *      token, mints an SMS OTP for the owner phone, and returns the
 *      masked phone the OTP went to.
 *   2. User enters OTP → POST /merchant/recover/verify-otp. Server
 *      validates OTP (5-attempt cap on otp_codes row), then in a
 *      single tx rotates tenants.api_token_hash + api_token_prefix
 *      and returns the plaintext token. THIS IS THE ONLY TIME THE
 *      TOKEN IS EVER EXPOSED — once the user navigates away it can
 *      only be rotated again, never re-fetched.
 *   3. Final screen — copy-able code block + stark warning that the
 *      OLD token is already invalid (D61: rotation is immediate, no
 *      grace period).
 *
 * Cancel from steps 1 or 2 calls /merchant/recover/cancel which
 * marks the request CANCELLED in the DB so a fresh request can be
 * started without waiting out the 15-minute expiry.
 */

type Step =
  | { kind: "verifying-link" }
  | { kind: "link-failed"; message: string }
  | {
      kind: "enter-otp";
      requestId: string;
      ownerPhoneMasked: string;
      expiresAt: string;
    }
  | { kind: "done"; newApiToken: string; apiTokenPrefix: string };

interface VerifyLinkOk {
  data: {
    requestId: string;
    expiresAt: string;
    ownerPhoneMasked: string;
  };
}

interface VerifyOtpOk {
  data: {
    newApiToken: string;
    apiTokenPrefix: string;
  };
}

interface ApiErr {
  error?: { code?: string; message?: string };
}

function getQueryParams(): { id: string | null; token: string | null } {
  const sp = new URLSearchParams(window.location.search);
  return { id: sp.get("id"), token: sp.get("token") };
}

export function MerchantTokenRecoveryVerifyPage() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<Step>({ kind: "verifying-link" });
  const [otp, setOtp] = useState("");
  const [otpErr, setOtpErr] = useState<string | null>(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Step 1 — verify the email link on mount. Run exactly once
  // (StrictMode-safe via the explicit "verifying-link" guard).
  useEffect(() => {
    if (step.kind !== "verifying-link") return;
    const { id, token } = getQueryParams();
    if (!id || !token) {
      setStep({
        kind: "link-failed",
        message:
          "This recovery link is missing required parts. Request a new one.",
      });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/v1/merchant/recover/verify-link", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ requestId: id, token }),
        });
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiErr | null;
          setStep({
            kind: "link-failed",
            message:
              body?.error?.message ??
              "This recovery link is no longer valid. Request a new one.",
          });
          return;
        }
        const body = (await res.json()) as VerifyLinkOk;
        setStep({
          kind: "enter-otp",
          requestId: body.data.requestId,
          ownerPhoneMasked: body.data.ownerPhoneMasked,
          expiresAt: body.data.expiresAt,
        });
      } catch {
        if (cancelled) return;
        setStep({
          kind: "link-failed",
          message: "Network error. Please try again in a moment.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step.kind]);

  async function onVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (step.kind !== "enter-otp") return;
    setOtpErr(null);
    const trimmed = otp.trim();
    if (!/^\d{6}$/u.test(trimmed)) {
      setOtpErr("Enter the 6-digit code from the SMS.");
      return;
    }
    setOtpBusy(true);
    try {
      const res = await fetch("/api/v1/merchant/recover/verify-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ requestId: step.requestId, otp: trimmed }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ApiErr | null;
        const code = body?.error?.code ?? "";
        if (code === "OTP_LOCKED") {
          setOtpErr(
            "Too many incorrect attempts. Cancel this request and start over.",
          );
        } else if (code === "OTP_EXPIRED" || code === "REQUEST_EXPIRED") {
          setOtpErr(
            "This recovery has expired. Cancel and start a fresh request.",
          );
        } else {
          setOtpErr(body?.error?.message ?? "That code wasn't accepted.");
        }
        return;
      }
      const body = (await res.json()) as VerifyOtpOk;
      setStep({
        kind: "done",
        newApiToken: body.data.newApiToken,
        apiTokenPrefix: body.data.apiTokenPrefix,
      });
    } catch {
      setOtpErr("Network error. Please try again in a moment.");
    } finally {
      setOtpBusy(false);
    }
  }

  async function onCancel() {
    if (step.kind !== "enter-otp") {
      navigate("/merchant/login");
      return;
    }
    if (
      !window.confirm(
        "Cancel this recovery? You'll need a fresh email link to start over.",
      )
    )
      return;
    try {
      await fetch("/api/v1/merchant/recover/cancel", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ requestId: step.requestId }),
      });
    } catch {
      // best-effort — the request will expire on its own anyway
    }
    navigate("/merchant/recover");
  }

  async function onCopy(token: string) {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard may be blocked; user can still triple-click and copy
    }
  }

  if (step.kind === "verifying-link") {
    return (
      <AuthLayout title="Verifying your link…">
        <p className="text-sm text-ink-dark">One moment.</p>
      </AuthLayout>
    );
  }

  if (step.kind === "link-failed") {
    return (
      <AuthLayout title="Link not valid" subtitle={step.message}>
        <Link
          href="/merchant/recover"
          className="btn-primary block w-full text-center"
        >
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  if (step.kind === "enter-otp") {
    return (
      <AuthLayout
        title="Confirm by SMS"
        subtitle={`We sent a 6-digit code to ${step.ownerPhoneMasked}.`}
      >
        <form onSubmit={onVerifyOtp} className="space-y-4">
          <div>
            <label htmlFor="recover-otp" className="label">
              SMS code
            </label>
            <input
              id="recover-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/gu, ""))}
              className="input font-mono text-lg tracking-widest text-center"
            />
            <p className="mt-1 text-xs text-ink-dark">
              Code is valid until{" "}
              {new Date(step.expiresAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
              . You have 5 attempts.
            </p>
          </div>
          {otpErr && (
            <p className="rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
              {otpErr}
            </p>
          )}
          <button
            type="submit"
            disabled={otpBusy}
            className="btn-primary w-full"
          >
            {otpBusy ? "Verifying…" : "Reveal new token"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="block w-full text-center text-sm text-navy underline"
          >
            Cancel recovery
          </button>
        </form>
      </AuthLayout>
    );
  }

  // step.kind === "done"
  return (
    <AuthLayout
      title="Your new token"
      subtitle="Copy it now and store it in your password manager."
    >
      <div className="space-y-4">
        <div className="rounded-card border border-amber-light bg-white p-3">
          <div className="text-xs text-ink-dark">Token prefix</div>
          <div className="font-mono text-sm">{step.apiTokenPrefix}</div>
        </div>
        <div>
          <label className="label">New API token</label>
          <div className="mt-1 break-all rounded-card border border-amber-light bg-white p-3 font-mono text-xs">
            {step.newApiToken}
          </div>
          <button
            type="button"
            onClick={() => onCopy(step.newApiToken)}
            className="btn-secondary mt-2 w-full"
          >
            {copied ? "Copied" : "Copy token"}
          </button>
        </div>
        <p className="rounded-card bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <strong>Important:</strong> your previous token has already been
          invalidated. Update every cashier device, server, and saved
          configuration with this new token before signing out — once you
          leave this page the token cannot be retrieved again, only rotated.
        </p>
        <Link
          href="/merchant/login"
          className="btn-primary block w-full text-center"
        >
          Done
        </Link>
      </div>
    </AuthLayout>
  );
}
